import {
  IPlatformAPI,
  IPlatformServices,
} from '@wix/native-components-infra/dist/src/types/types';
import { IHttpClient } from '@wix/http-client';
import { getEcommerceSettings } from '@wix/ambassador-ecom-v1-ecommerce-settings/http';
import {
  CheckoutBillingFields,
  CheckoutBrand,
  CheckoutFields,
  CheckoutPolicies,
  CountryDeliveryProperties,
  DeliveryDestinationProperties,
  GetEcommerceSettingsResponse,
  RequestedFields,
} from '@wix/ambassador-ecom-v1-ecommerce-settings/types';
import {
  hasAdditionalFees,
  hasRequestedFieldError,
  hasValidationsInCart,
} from './EcommerceSettingsService.utils';
import { SiteStore } from '@wix/wixstores-client-storefront-sdk';
import { ViewerStorage } from '../viewer-storage/ViewerStorage';
import { EcomPlatformViewerScriptContext } from '../../viewer-script/types';

const ECOMMERCE_SETTINGS_CACHE_KEY = 'SPI_CONFIG_PROPERTIES';

export type EcommerceSettings = {
  hasValidationsInCart: boolean;
  hasAdditionalFees: boolean;
  isPremium?: boolean;
  delivery: {
    countries?: CountryDeliveryProperties[];
    restOfWorld?: DeliveryDestinationProperties;
  };
  tax: {
    taxIncludedInItemPrices?: boolean;
    configured?: boolean;
  };
  businessInfo?: {
    storeName?: string | null;
    logo?: string | null;
  };
  checkoutSettings?: {
    checkoutPolicies?: CheckoutPolicies;
    checkoutFields?: CheckoutFields;
    checkoutBrand?: CheckoutBrand;
    checkoutBillingFields?: CheckoutBillingFields;
  };
};

export class EcommerceSettingsService {
  private readonly apis: IPlatformAPI;
  private readonly httpClient: IHttpClient;
  private readonly siteStore: SiteStore;
  private ecommerceSettingsPromise?: Promise<GetEcommerceSettingsResponse>;
  private readonly errorHandler: IPlatformServices['essentials']['errorHandler'];
  private readonly viewerStorage: ViewerStorage<GetEcommerceSettingsResponse>;

  constructor({
    context,
    isLightbox,
    apis,
    platformServices,
  }: {
    context: EcomPlatformViewerScriptContext;
    apis: IPlatformAPI;
    isLightbox: boolean;
    platformServices: IPlatformServices;
  }) {
    this.apis = apis;
    this.siteStore = context.siteStore;
    this.viewerStorage = new ViewerStorage({
      cacheKey: ECOMMERCE_SETTINGS_CACHE_KEY,
      clearOnRefresh: !isLightbox,
      storage: this.apis.storage,
      siteStore: this.siteStore,
    });
    this.httpClient = platformServices.essentials.httpClient;
    this.errorHandler = platformServices.essentials.errorHandler;
  }

  public init() {
    this.ecommerceSettingsPromise =
      this.getEcommerceSettingsFromServerOrCache();
    this.ecommerceSettingsPromise.catch(() => {
      //
    });
  }

  public async getEcommerceSettings(): Promise<EcommerceSettings> {
    if (!this.ecommerceSettingsPromise) {
      this.ecommerceSettingsPromise =
        this.getEcommerceSettingsFromServerOrCache();
    }

    let ecommerceSettingsResponse;
    try {
      ecommerceSettingsResponse = await this.ecommerceSettingsPromise;
    } catch (error) {
      ecommerceSettingsResponse = {
        ecommerceSettings: undefined,
        errors: [
          { requestedField: RequestedFields.DELIVERY },
          { requestedField: RequestedFields.ADDITIONAL_FEES_SERVICE_PLUGIN },
          { requestedField: RequestedFields.VALIDATIONS_SERVICE_PLUGIN },
          { requestedField: RequestedFields.BUSINESS_INFO },
          { requestedField: RequestedFields.CHECKOUT },
          { requestedField: RequestedFields.PREMIUM_SITE },
          { requestedField: RequestedFields.TAX },
        ],
      };
    }

    return {
      hasAdditionalFees: hasAdditionalFees(ecommerceSettingsResponse),
      hasValidationsInCart: hasValidationsInCart(ecommerceSettingsResponse),
      isPremium: !hasRequestedFieldError(
        ecommerceSettingsResponse.errors,
        RequestedFields.PREMIUM_SITE,
      )
        ? Boolean(ecommerceSettingsResponse.ecommerceSettings?.premiumSite)
        : true,
      delivery: this.getDeliveryConfiguration(ecommerceSettingsResponse),
      tax: {
        taxIncludedInItemPrices: Boolean(
          ecommerceSettingsResponse.ecommerceSettings?.tax
            ?.taxIncludedInItemPrices,
        ),
        configured: Boolean(
          ecommerceSettingsResponse.ecommerceSettings?.tax?.configured ?? true,
        ),
      },
      businessInfo: {
        storeName:
          ecommerceSettingsResponse.ecommerceSettings?.businessInfo?.name,
        logo: ecommerceSettingsResponse.ecommerceSettings?.businessInfo?.logo,
      },
      checkoutSettings: {
        ...ecommerceSettingsResponse.ecommerceSettings?.checkout,
      },
    };
  }

  private getDeliveryConfiguration(
    ecommerceSettingsResponse: GetEcommerceSettingsResponse,
  ) {
    if (
      hasRequestedFieldError(
        ecommerceSettingsResponse.errors,
        RequestedFields.DELIVERY,
      )
    ) {
      return {
        countries: [],
        restOfWorld: { pickupOnly: false, postalCodeRequired: false },
      };
    }

    return {
      countries:
        ecommerceSettingsResponse.ecommerceSettings?.delivery?.countries,
      restOfWorld:
        ecommerceSettingsResponse.ecommerceSettings?.delivery?.restOfWorld,
    };
  }

  private async getEcommerceSettingsFromServerOrCache() {
    const cachedEcommerceSettings = this.viewerStorage.get();

    if (cachedEcommerceSettings) {
      return cachedEcommerceSettings;
    }

    const ecommerceSettings = (
      await this.errorHandler.withErrorHandler(
        () => this.ecommerceSettingsRequest(),
        { errorCodesMap: {} },
      )
    ).data;

    this.viewerStorage.set(ecommerceSettings);

    return ecommerceSettings;
  }

  private readonly ecommerceSettingsRequest = () => {
    return this.httpClient.request(
      getEcommerceSettings({
        fields: [
          RequestedFields.ADDITIONAL_FEES_SERVICE_PLUGIN,
          RequestedFields.VALIDATIONS_SERVICE_PLUGIN,
          RequestedFields.TAX,
          RequestedFields.CHECKOUT,
          RequestedFields.BUSINESS_INFO,
          RequestedFields.DELIVERY,
          RequestedFields.PREMIUM_SITE,
        ],
      }),
    );
  };
}
